import { MoreVertical, Trash2 } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`

const SectionSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.5;

  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const DeviceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const DeviceButton = styled.button<{ $connected?: boolean }>`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 2px solid ${({ theme, $connected }) => ($connected ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  width: 100%;
  text-align: center;
  position: relative;
  padding: ${({ theme }) => theme.spacing.lg};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const DeviceLogo = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  flex-grow: 1;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const ConnectedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  left: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.xs};
  font-weight: 600;
`

const Message = styled.div<{ $type: 'error' | 'success' }>`
  color: ${({ theme, $type }) => ($type === 'error' ? theme.colors.error : theme.colors.primary)};
  font-size: ${({ theme }) => theme.typography.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme, $type }) => ($type === 'error' ? theme.colors.error + '10' : theme.colors.primary + '10')};
`

const MenuContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
`

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.default};
  z-index: 10;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
`

const MenuItem = styled.button<{ $destructive?: boolean }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: transparent;
  border: none;
  color: ${({ theme, $destructive }) => ($destructive ? theme.colors.error : theme.colors.text)};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`

const ConfirmDialogContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const ConfirmDialogTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`

const ConfirmDialogMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: 1.5;
`

const ConfirmDialogActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`

const ConfirmButton = styled.button<{ $variant: 'primary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: none;
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
    &:hover {
      background: ${theme.colors.border};
    }
  `
      : `
    background: ${theme.colors.error};
    color: white;
    &:hover {
      opacity: 0.9;
    }
  `}
`

interface ConnectDeviceProps {
  className?: string
}

/**
 * Generate a secret code verifier and challenge.
 */
function generateCodeVerifier(len = 64) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const array = new Uint8Array(len)
  crypto.getRandomValues(array)
  return Array.from(array, (x) => chars[x % chars.length]).join('')
}

/**
 * For devices that can perform a SHA256 hash,
 * the code challenge is a BASE64-URL-encoded string of the SHA256 hash of the code verifier.
 */
async function pkceChallengeFromVerifier(verifier: string) {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(digest))
}

function base64UrlEncode(bytes: Uint8Array): string {
  const b64 = btoa(String.fromCharCode(...bytes))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const ConnectDevice: React.FC<ConnectDeviceProps> = ({ className }) => {
  const { user } = useAuth()
  const [deviceConnections, setDeviceConnections] = useState<Record<string, boolean>>({})
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [codeChallenge, setCodeChallenge] = useState<string | null>(null)
  const [codeVerifier, setCodeVerifier] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState<string | null>(null)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Load device connections when component mounts
  useEffect(() => {
    const loadDeviceConnections = async () => {
      if (!user) return

      try {
        const { data, error } = await supabase
          .from('device_connections')
          .select('device_type, is_active')
          .eq('user_id', user.id)
          .eq('is_active', true)

        if (error) {
          console.error('Error loading device connections:', error)
          return
        }

        const connections: Record<string, boolean> = {}
        data?.forEach((connection) => {
          connections[connection.device_type] = true
        })
        setDeviceConnections(connections)
      } catch (error) {
        console.error('Error loading device connections:', error)
      }
    }

    loadDeviceConnections()
  }, [user])

  useEffect(() => {
    // pre-generate code verifier and challenge so that
    // there's no async operation before opening the popup.
    // this is to avoid the "popup blocked" error.
    const codeVerifier = generateCodeVerifier()
    setCodeVerifier(codeVerifier)
    pkceChallengeFromVerifier(codeVerifier).then((challenge) => {
      setCodeChallenge(challenge)
    })
  }, [])

  const devices = [
    {
      name: 'Garmin',
      description: 'Connect your Garmin device',
      icon: <img src="/garmin-logo.png" alt="Garmin" width={140} height={60} />,
      implemented: true,
      action: () => handleDeviceConnect('garmin'),
    },
    {
      name: 'Apple Watch',
      description: 'Sync Apple Health data',
      icon: <img src="/apple-watch-logo.png" alt="Apple Watch" width={140} height={60} />,
      implemented: false,
      action: () => handleDeviceConnect('apple'),
    },
    {
      name: 'Wahoo',
      description: 'Connect Wahoo devices',
      icon: <img src="/wahoo-logo.png" alt="Wahoo" width={140} height={60} />,
      implemented: false,
      action: () => handleDeviceConnect('wahoo'),
    },
    {
      name: 'Suunto',
      description: 'Connect Suunto devices',
      icon: <img src="/suunto-logo.png" alt="Suunto" width={140} height={60} />,
      implemented: false,
      action: () => handleDeviceConnect('suunto'),
    },
    {
      name: 'Polar',
      description: 'Connect Polar devices',
      icon: <img src="/polar-logo.png" alt="Polar" width={140} height={60} />,
      implemented: false,
      action: () => handleDeviceConnect('polar'),
    },
  ]

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleDeviceConnect = async (deviceType: string) => {
    if (deviceType === 'garmin') {
      await handleGarminConnect()
    } else {
      console.log(`Connecting to ${deviceType}...`)
      setMessage({ type: 'success', text: `${deviceType} connection will be implemented soon` })
    }
  }

  const handleMenuClick = (e: React.MouseEvent, deviceType: string) => {
    e.stopPropagation()
    setMenuOpen(menuOpen === deviceType ? null : deviceType)
  }

  const handleRemoveClick = (e: React.MouseEvent, deviceType: string) => {
    e.stopPropagation()
    setMenuOpen(null)
    setShowRemoveConfirm(deviceType)
  }

  const handleConfirmRemove = async (deviceType: string) => {
    if (!user) return

    try {
      // Deactivate the device connection
      const { error } = await supabase
        .from('device_connections')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('device_type', deviceType)

      if (error) {
        console.error('Error removing device connection:', error)
        setMessage({ type: 'error', text: `Failed to remove ${deviceType} connection` })
      } else {
        setMessage({ type: 'success', text: `${deviceType} connection removed successfully` })
        setDeviceConnections((prev) => ({ ...prev, [deviceType]: false }))
      }
    } catch (error) {
      console.error('Error removing device connection:', error)
      setMessage({ type: 'error', text: `Failed to remove ${deviceType} connection` })
    } finally {
      setShowRemoveConfirm(null)
    }
  }

  const handleCancelRemove = () => {
    setShowRemoveConfirm(null)
  }

  const handleGarminConnect = async () => {
    if (!user) {
      setMessage({ type: 'error', text: 'Please sign in to connect Garmin' })
      return
    }

    try {
      setIsConnecting(true)
      setMessage({ type: 'success', text: 'Please login to Garmin Connect in the separate window' })

      // Request authorization from Garmin Connect
      const clientId = process.env.REACT_APP_GARMIN_CLIENT_ID
      if (!clientId) {
        setMessage({ type: 'error', text: 'GARMIN_CLIENT_ID is not defined in the environment variables' })
        return
      }

      if (!codeChallenge || !codeVerifier) {
        setMessage({ type: 'error', text: 'Code challenge or verifier is not available' })
        return
      }

      // Generate a random string to use for the 'state'.
      const state = crypto.randomUUID()
      if (!state) {
        setMessage({ type: 'error', text: 'Failed to generate cryptographically secure random string for state' })
        return
      }
      const redirectUri = `${window.location.origin}/garmin-callback`

      const authUrl = new URL('https://connect.garmin.com/oauth2Confirm')
      authUrl.searchParams.append('client_id', clientId)
      authUrl.searchParams.append('response_type', 'code')
      authUrl.searchParams.append('state', state)
      authUrl.searchParams.append('redirect_uri', redirectUri)
      authUrl.searchParams.append('code_challenge', codeChallenge)
      authUrl.searchParams.append('code_challenge_method', 'S256')

      // Store user data for callback handling.
      // use localStorage because on mobile, if the garmin app is installed
      // a new window is opened for the callback, which cannot access sessionStorage.
      localStorage.setItem('garmin_auth_code_verifier', codeVerifier)
      localStorage.setItem('garmin_auth_state', state)
      localStorage.setItem('garmin_auth_callback_page', 'true')

      // Open Garmin authorization in new window
      console.log('authUrl', authUrl.toString())
      const authWindow = window.open(
        authUrl.toString(),
        'garmin-auth',
        'width=600,height=900,scrollbars=yes,resizable=yes'
      )

      if (!authWindow) {
        setMessage({ type: 'error', text: 'Please allow popups for Garmin authentication' })
        return
      }

      // Listen for authorization completion
      const checkClosed = setInterval(() => {
        const authSuccess = localStorage.getItem('garmin_auth_success')
        if (authSuccess === 'true') {
          setDeviceConnections((prev) => ({ ...prev, garmin: true }))
        }
      }, 5000)
      /*
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          console.log('Garmin window closed')
          clearInterval(checkClosed)
          // Check if authorization was successful
          const authSuccess = localStorage.getItem('garmin_auth_success')
          if (authSuccess === 'true') {
            setMessage({ type: 'success', text: 'Garmin connected successfully!' })
            // TODO localStorage.removeItem('garmin_auth_success')
            // Reload device connections to show updated status
            setDeviceConnections((prev) => ({ ...prev, garmin: true }))
          } else {
            setMessage({ type: 'error', text: 'Garmin authorization was cancelled or failed' })
          }
          // TODO localStorage.removeItem('garmin_auth_state')
          // TODO localStorage.removeItem('garmin_auth_callback_page')
          setIsConnecting(false)
        }
      }, 1000)

      // Cleanup if window doesn't close after 5 minutes
      setTimeout(() => {
        clearInterval(checkClosed)
        localStorage.removeItem('garmin_auth_success')
        localStorage.removeItem('garmin_auth_state')
        localStorage.removeItem('garmin_auth_callback_page')
        setIsConnecting(false)
      }, 300000)
       */
      setTimeout(() => {
        clearInterval(checkClosed)
      }, 300000)
    } catch (error) {
      console.error('Garmin connection error:', error)
      setMessage({ type: 'error', text: 'Failed to initiate Garmin connection' })
      setIsConnecting(false)
    }
  }

  return (
    <Container className={className}>
      <Section>
        <SectionTitle>Connect Your Devices</SectionTitle>
        <SectionSubtitle>
          Connect your fitness devices to automatically sync workouts and track your training progress. This will allow
          you and your coach to see your progress and provide personalized insights. Your data will be securely stored
          and handled according to our{' '}
          <a href="https://www.supacoach.ai/privacy/" target="_blank" rel="noopener noreferrer">
            privacy policy
          </a>
          .
        </SectionSubtitle>
        <DeviceGrid>
          {devices.map((device) => {
            const deviceType = device.name.toLowerCase()
            const isConnected = deviceConnections[deviceType]

            return (
              <DeviceButton
                key={device.name}
                onClick={device.action}
                disabled={!device.implemented || isConnecting}
                $connected={isConnected}
                title={isConnected ? `${device.name} is connected` : `Connect ${device.name}`}
              >
                {isConnected && <ConnectedBadge>Connected</ConnectedBadge>}
                {isConnected && (
                  <MenuContainer ref={menuRef}>
                    <MenuButton onClick={(e) => handleMenuClick(e, deviceType)} title="More options">
                      <MoreVertical size={20} />
                    </MenuButton>
                    {menuOpen === deviceType && (
                      <DropdownMenu>
                        <MenuItem $destructive onClick={(e) => handleRemoveClick(e, deviceType)}>
                          <Trash2 />
                          Remove Connection
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </MenuContainer>
                )}
                <DeviceLogo>{device.icon}</DeviceLogo>
              </DeviceButton>
            )
          })}
        </DeviceGrid>
        {message && <Message $type={message.type}>{message.text}</Message>}
      </Section>

      {/* Remove Confirmation Dialog */}
      {showRemoveConfirm && (
        <ConfirmDialog onClick={handleCancelRemove}>
          <ConfirmDialogContent onClick={(e) => e.stopPropagation()}>
            <ConfirmDialogTitle>Confirm Remove Device Connection</ConfirmDialogTitle>
            <ConfirmDialogMessage>
              Removing your {showRemoveConfirm} connection will stop syncing data from your device. You can reconnect at
              any time.
            </ConfirmDialogMessage>
            <ConfirmDialogActions>
              <ConfirmButton $variant="primary" onClick={handleCancelRemove}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $variant="danger" onClick={() => handleConfirmRemove(showRemoveConfirm)}>
                Remove Device Connection
              </ConfirmButton>
            </ConfirmDialogActions>
          </ConfirmDialogContent>
        </ConfirmDialog>
      )}
    </Container>
  )
}
